import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { companyService } from '../services/companyService';
import { userService } from '../services/userService';
import { getTimezoneForCountry } from '../utils/countryTimezoneMap';

const CompanyContext = createContext(null);

const DEFAULT_DISPLAY_PREFS = {
  dateFormat: 'MM/DD/YYYY',
  language: 'en',
};

export const CompanyProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [displayPrefs, setDisplayPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem('displayPreferences');
      return saved ? { ...DEFAULT_DISPLAY_PREFS, ...JSON.parse(saved) } : DEFAULT_DISPLAY_PREFS;
    } catch {
      return DEFAULT_DISPLAY_PREFS;
    }
  });
  const [loading, setLoading] = useState(true);

  const fetchCompany = useCallback(async () => {
    try {
      setLoading(true);
      const [companyRes, userRes] = await Promise.all([
        companyService.getCurrentCompany(),
        userService.getCurrentUser(),
      ]);
      setCompany(companyRes);
      setCurrentUserProfile(userRes);
      setIsCompanyAdmin(
        userRes.companyRole === 'COMPANY_ADMIN' || userRes.companyRole === 'COMPANY_OWNER'
      );
    } catch (err) {
      console.error('CompanyContext: Failed to load company data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const updateCompany = useCallback((updatedCompany) => {
    setCompany(updatedCompany);
  }, []);

  const updatePreferences = useCallback((prefs) => {
    const merged = { ...displayPrefs, ...prefs };
    setDisplayPrefs(merged);
    localStorage.setItem('displayPreferences', JSON.stringify(merged));
  }, [displayPrefs]);

  // Personal setting wins over the company default; country is a best-effort
  // guess only (company.country is free text, lookup returns null on a miss).
  const timezone =
    currentUserProfile?.timezone ||
    company?.timezone ||
    getTimezoneForCountry(company?.country) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const value = {
    company,
    currentUserProfile,
    timezone,
    dateFormat: displayPrefs.dateFormat,
    language: displayPrefs.language,
    isCompanyAdmin,
    loading,
    updateCompany,
    updatePreferences,
    refreshCompany: fetchCompany,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export default CompanyContext;
